// ---- Journal (client) ----
// A book the player can open with J (or the 📖 button). It overlays a centered two-page
// book: the left page is a table of contents (About Me / Skills / Quests), the right
// page renders the selected section. Opening it locks input + reframes the camera to a
// "reading" pose (game.ts drives the camera off game.journalOpen, like game.talking),
// and sets player.userData.reading so updatePlayer holds the book-reading arm pose.
//
// About Me mounts a small WebGL character showcase (real buildPlayer rig + gear) that
// spins idly and can be drag-rotated — disposed whenever the page leaves About Me.
//
// Quest data is server-authoritative and lives in quest.ts (fed by net.ts); this module
// is purely the view. It re-renders on open and whenever the player switches sections.

import * as THREE from "three";
import { game, inputLocked } from "./state.ts";
import { me, sendUnlockBox, sendQuestDev, serverDevCommands } from "./net.ts";
import { equippedType, equippedShieldType, equippedTool } from "./inventory.ts";
import { itemSpec, itemSpecOrNull } from "./items/index.ts";
import { resetJournalCamera } from "./camera.ts";
import { activeQuests, completedQuests, journalEntries, killCount, currentWaypoint, type QuestView } from "./quest.ts";
import { QUESTS } from "./quest/index.ts";
import type { QuestSpec } from "./quest/define.ts";
import { NPCS } from "./npc/roster.ts";
import { skillXp, skillBoxes, influenceAmount, standingOf, equippedTitle, barLabel } from "./skill.ts";
import {
  SKILLS, STANDINGS, standingById, boxOf, checkBox, investedIn, rungNameForXp,
  type SkillSpec, type SkillBox,
} from "./skill/index.ts";
import { PROGRESSION_DEFAULTS, WEAR_SLOTS, type WearSlot } from "./shared.ts";
import { wornGear } from "./gear.ts";
import { walletObol } from "./wallet.ts";
import { buildPlayer, setWeapon, setShield, setTool, updatePlayer } from "./player.ts";
import { disposeGroup } from "./meshkit.ts";
import { FOV } from "./config.ts";
import { toCssHex } from "./color.ts";
import { MOVES } from "./moves.ts";
import { makeSkillCrestEl, makeBranchCrestEl, warmSkillIcons } from "./ui-icons.ts";

const JOURNAL_EXIT_DUR = 0.5; // camera ease-back window after closing (matches talk's)
type Section = "quests" | "skills" | "about";
let section: Section = "about";
/** When on Skills: null = overview of all arts; else the skill id being drilled into. */
let skillFocus: string | null = null;
/** Within a multi-branch skill, the active branch tab (e.g. "melee" / "ranged"). */
let skillBranch: string | null = null;
/** When set, the right page shows one box's detail (under the focused skill). */
let skillBoxFocus: string | null = null;
/** When on Quests: null = card overview; else the quest id being drilled into. */
let questFocus: string | null = null;

let journalEl: HTMLElement | null = null;
let tocEl: HTMLElement | null = null;
let pageEl: HTMLElement | null = null; // the right (content) page

/** Live About Me avatar (own GL context). Null when not on that page. */
let aboutAvatar: { dispose: () => void } | null = null;

export function initJournal(): void {
  journalEl = document.getElementById("journal");
  tocEl = journalEl?.querySelector(".toc") as HTMLElement | null;
  pageEl = journalEl?.querySelector(".page.right") as HTMLElement | null;

  document.getElementById("journalToggle")?.addEventListener("click", toggleJournal);
  journalEl?.querySelector(".close")?.addEventListener("click", () => { if (game.journalOpen) toggleJournal(); });

  // Esc: box detail → skill tree → skills overview → close journal.
  // Capture phase + stopImmediatePropagation so focus/combat Esc handlers (registered
  // earlier on bubble) don't also clear world focus while the book owns Esc.
  addEventListener("keydown", (e) => {
    if (e.key !== "Escape" || !game.journalOpen) return;
    e.stopImmediatePropagation();
    if (section === "skills" && skillBoxFocus) {
      closeSkillBox();
      return;
    }
    if (section === "skills" && skillFocus) {
      skillFocus = null;
      skillBranch = null;
      skillBoxFocus = null;
      render();
      return;
    }
    if (section === "quests" && questFocus) {
      questFocus = null;
      render();
      return;
    }
    toggleJournal();
  }, true);
}

/** Open/close the book. Blocked while signed-out, knocked down, or mid-conversation. */
export function toggleJournal(): void {
  if (!game.journalOpen) {
    if (!game.joined || inputLocked()) return; // any activity owning input + camera blocks opening the book
    game.journalOpen = true;
    section = "about";
    skillFocus = null;
    skillBranch = null;
    skillBoxFocus = null;
    questFocus = null;
    // Skill crests: first open only — never at boot (see ui-icons.ts).
    warmSkillIcons();
    resetJournalCamera(); // ease the reading framing in from the live view
    render();
    if (journalEl) journalEl.style.display = "flex";
  } else {
    game.journalOpen = false;
    skillFocus = null;
    skillBranch = null;
    skillBoxFocus = null;
    questFocus = null;
    disposeAboutAvatar();
    game.journalExit = JOURNAL_EXIT_DUR; // ease the camera back (game.ts)
    if (journalEl) journalEl.style.display = "none";
  }
  document.getElementById("journalToggle")?.classList.toggle("active", game.journalOpen);
  if (game.player) game.player.userData.reading = game.journalOpen; // drives the reading pose
}

function setSection(s: Section): void {
  section = s;
  skillFocus = null; // always land on the section root (overview, not a drill-in)
  skillBranch = null;
  skillBoxFocus = null;
  questFocus = null;
  render();
}

function openSkill(id: string): void {
  skillFocus = id;
  skillBranch = defaultBranch(id);
  skillBoxFocus = null;
  render();
}

function closeSkill(): void {
  skillFocus = null;
  skillBranch = null;
  skillBoxFocus = null;
  render();
}

function setSkillBranch(key: string): void {
  skillBranch = key;
  skillBoxFocus = null; // leave box detail when switching paths
  render();
}

function openSkillBox(boxId: string): void {
  skillBoxFocus = boxId;
  render();
}

function closeSkillBox(): void {
  skillBoxFocus = null;
  render();
}

/** First authored branch for a skill (tabs land here on open). */
function defaultBranch(skillId: string): string | null {
  const spec = SKILLS.find((s) => s.id === skillId);
  if (!spec) return null;
  if (spec.branches?.length) return spec.branches[0]!;
  const fromBox = spec.boxes.find((b) => b.branch)?.branch;
  return fromBox ?? null;
}

function openQuest(id: string): void {
  questFocus = id;
  render();
}

function closeQuest(): void {
  questFocus = null;
  render();
}

/** Shared "‹ Back" control for skill / quest drill-ins. */
function backButton(label: string, onClick: () => void): HTMLButtonElement {
  const back = el("button", "journal-back") as HTMLButtonElement;
  back.type = "button";
  back.innerHTML = `<span class="bk-arr">‹</span> ${label}`;
  back.addEventListener("click", onClick);
  return back;
}

function render(): void {
  renderToc();
  renderPage();
}

function renderToc(): void {
  if (!tocEl) return;
  const items: { id: Section; label: string; count?: number }[] = [
    { id: "about", label: "About Me" },
    { id: "skills", label: "Skills", count: influenceAmount() || undefined }, // count = spendable points
    { id: "quests", label: "Quests", count: activeQuests().length || undefined },
  ];
  tocEl.replaceChildren();
  for (const it of items) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = it.id === section ? "sel" : "";
    b.innerHTML = it.label + (it.count !== undefined ? ` <span class="count">(${it.count})</span>` : "");
    b.addEventListener("click", () => setSection(it.id));
    tocEl.appendChild(b);
  }
}

function el(tag: string, cls?: string, html?: string): HTMLElement {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (html !== undefined) e.innerHTML = html;
  return e;
}

function renderPage(): void {
  if (!pageEl) return;
  // Tear down any prior About Me GL showcase before wiping the page DOM —
  // replaceChildren alone leaves a live RAF + WebGL context orphaned.
  disposeAboutAvatar();
  pageEl.replaceChildren();
  if (section === "quests") pageEl.append(...questsPage());
  else if (section === "skills") pageEl.append(...skillsPage());
  else pageEl.append(...aboutPage());
}

function disposeAboutAvatar(): void {
  aboutAvatar?.dispose();
  aboutAvatar = null;
}

/** Re-render the open book (no-op when closed). Called from net.ts on the
 *  skills/influence/standing messages so spending a point / gaining XP updates
 *  the page live — same pattern as shop.ts's refreshShopIfOpen. */
export function refreshJournalIfOpen(): void {
  if (game.journalOpen && pageEl) render();
}

/** Memoized quest rewards — content is static after load. */
const questRewardCache = new Map<string, { items: string[]; obol: number }>();

/**
 * Items + pay granted by a quest, derived from content (not a live inventory
 * audit): dialogue `giveItem` on the immediate post start/advance node (and
 * choices whose `when` gates this quest), plus any step `reward.obol`.
 */
function questRewards(spec: QuestSpec): { items: string[]; obol: number } {
  const hit = questRewardCache.get(spec.id);
  if (hit) return hit;

  // First-seen order (walk / content order), not alpha.
  const items: string[] = [];
  const seenType = new Set<string>();
  const addItem = (type: string) => {
    if (seenType.has(type)) return;
    seenType.add(type);
    items.push(type);
  };

  // One pass per kind (spawns share talk docs; kind avoids N×spawn rescans).
  const seenKind = new Set<string>();
  for (const npc of NPCS) {
    const talk = npc.talk;
    if (!talk) continue;
    const kindKey = npc.kind ?? npc.id;
    if (seenKind.has(kindKey)) continue;
    seenKind.add(kindKey);

    const byId = new Map(talk.nodes.map((n) => [n.id, n] as const));
    for (const node of talk.nodes) {
      for (const c of node.choices ?? []) {
        const a = c.action;
        if (!a) continue;
        // Gift gated directly on this quest (re-offer lines).
        if (a.kind === "giveItem" && c.when?.quest === spec.id && c.when.is !== "completed") {
          addItem(a.type);
        }
        // Hand-off nodes: startQuest/advanceQuest → immediate `to` only (no deep BFS).
        if ((a.kind === "startQuest" || a.kind === "advanceQuest") && a.id === spec.id && c.to) {
          const dest = byId.get(c.to);
          if (!dest) continue;
          for (const dc of dest.choices ?? []) {
            if (dc.action?.kind === "giveItem") addItem(dc.action.type);
          }
        }
      }
    }
  }

  let obol = 0;
  for (const step of spec.steps) {
    if (step.reward?.obol) obol += step.reward.obol;
  }

  const result = { items, obol };
  questRewardCache.set(spec.id, result);
  return result;
}

/** Rewards strip: gifted items + obol pay (labeled Received once the quest is done). */
function questRewardsEl(spec: QuestSpec, done: boolean): HTMLElement | null {
  const { items, obol } = questRewards(spec);
  if (!items.length && !obol) return null;

  const wrap = el("div", `quest-rewards${done ? " received" : ""}`);
  wrap.appendChild(el("div", "qr-title", done ? "Received" : "Rewards"));
  const list = el("div", "qr-list");
  for (const type of items) {
    const name = itemSpecOrNull(type)?.name ?? type;
    const chip = el("span", "qr-item", name);
    chip.title = type;
    list.appendChild(chip);
  }
  if (obol > 0) {
    list.appendChild(el("span", "qr-item obol", `${fmt(obol)} obol`));
  }
  wrap.appendChild(list);
  return wrap;
}

/** A quest block: title, summary, an optional completed stamp, the step checklist, and
 *  the diary notes logged for that quest. Shared by the Quests detail view. */
function questBlock(v: QuestView, done: boolean): HTMLElement {
  const wrap = el("div", "quest detail");
  wrap.appendChild(el("h3", undefined, v.spec.title));
  if (done) wrap.appendChild(el("div", "stamp", "✓ Completed"));
  wrap.appendChild(el("div", "summary", v.spec.summary));

  // Call out the live waypoint on active quests — mirrors the top-center tracker
  // so the journal and world HUD stay in lockstep.
  if (!done) {
    const activeStep = v.steps.find((s) => s.state === "active");
    if (activeStep) {
      wrap.appendChild(el("div", "waypoint",
        `<span class="wp-icon">◆</span><span class="wp-label">Current objective</span>` +
        `<span class="wp-desc">${activeStep.desc}</span>`));
    }
  }

  // Gifts + pay for this quest (from content: giveItem paths + step reward.obol).
  const rewards = questRewardsEl(v.spec, done);
  if (rewards) wrap.appendChild(rewards);

  const ul = el("ul", "steps");
  for (const s of v.steps) {
    const tick = s.state === "done" ? "✓" : s.state === "active" ? "◆" : "·";
    const li = el("li", s.state);
    const tag = s.state === "active" ? ` <span class="wp-tag">waypoint</span>` : "";
    li.innerHTML = `<span class="tick">${tick}</span><span>${s.desc}${tag}</span>`;
    // Dev: click a step to jump the waypoint there (DEV_COMMANDS server).
    if (serverDevCommands) {
      li.classList.add("dev-step");
      li.title = `DEV: jump to "${s.id}"`;
      li.style.cursor = "pointer";
      li.addEventListener("click", (e) => {
        e.stopPropagation();
        sendQuestDev(v.spec.id, s.id);
      });
    }
    ul.appendChild(li);
  }
  wrap.appendChild(ul);

  if (serverDevCommands) {
    wrap.appendChild(devQuestTools(v.spec.id, done ? "completed" : "active"));
  }

  const notes = journalEntries().filter((e) => e.questId === v.spec.id);
  if (notes.length) {
    const n = el("div", "notes");
    n.appendChild(el("div", "notes-title", "Journal entries"));
    for (const entry of notes) n.appendChild(el("div", "note", entry.text));
    wrap.appendChild(n);
  }
  return wrap;
}

/** Overview tile — click to open the full quest log. */
function questOverviewCard(v: QuestView, done: boolean): HTMLElement {
  const doneN = v.steps.filter((s) => s.state === "done").length;
  const total = v.steps.length;
  const activeStep = v.steps.find((s) => s.state === "active");
  const tracking = !done && currentWaypoint()?.questId === v.spec.id;
  const frac = total > 0 ? (done ? 1 : doneN / total) : 0;

  const card = el("button",
    `quest-card${done ? " done" : ""}${tracking ? " tracking" : ""}`) as HTMLButtonElement;
  card.type = "button";

  const top = el("div", "card-top");
  const crest = el("div", "quest-crest", done ? "✓" : tracking ? "◆" : "📜");
  crest.setAttribute("aria-hidden", "true");
  top.appendChild(crest);
  const identity = el("div", "quest-identity");
  identity.appendChild(el("h3", undefined, v.spec.title));
  if (done) {
    identity.appendChild(el("div", "quest-status done", "Completed"));
  } else if (tracking) {
    identity.appendChild(el("div", "quest-status tracking", "Currently tracking"));
  } else {
    identity.appendChild(el("div", "quest-status", `Step ${Math.min(doneN + 1, total)} of ${total}`));
  }
  top.appendChild(identity);
  top.appendChild(el("span", "card-chev", "›"));
  card.appendChild(top);

  card.appendChild(el("div", "card-summary", v.spec.summary));

  // Progress bar toward completion.
  const prog = el("div", "quest-prog");
  const bar = el("div", "quest-prog-bar");
  const fill = el("span", "fill");
  fill.style.width = `${(frac * 100).toFixed(1)}%`;
  bar.appendChild(fill);
  prog.appendChild(bar);
  prog.appendChild(el("span", "quest-prog-num",
    done ? `${total}/${total}` : `${doneN}/${total}`));
  card.appendChild(prog);

  if (!done && activeStep) {
    card.appendChild(el("div", "card-objective",
      `<span class="co-k">Objective</span><span class="co-d">${activeStep.desc}</span>`));
  }

  // Compact rewards on the card (items + pay) so completed deeds show the haul.
  const { items, obol } = questRewards(v.spec);
  if (items.length || obol > 0) {
    const row = el("div", "card-rewards");
    row.appendChild(el("span", "cr-k", done ? "Received" : "Rewards"));
    const chips = el("span", "cr-list");
    for (const type of items) {
      chips.appendChild(el("span", "cr-item", itemSpecOrNull(type)?.name ?? type));
    }
    if (obol > 0) chips.appendChild(el("span", "cr-item obol", `${fmt(obol)} obol`));
    row.appendChild(chips);
    card.appendChild(row);
  } else if (done) {
    card.appendChild(el("div", "card-hint", "Review the full log ›"));
  } else {
    card.appendChild(el("div", "card-hint", "Open quest log ›"));
  }

  card.addEventListener("click", () => openQuest(v.spec.id));
  return card;
}

/** Dev-only scrubber row: restart / jump chips / complete / unstart. */
function devQuestTools(questId: string, status: "active" | "completed" | "none"): HTMLElement {
  const bar = el("div", "dev-quest");
  bar.appendChild(el("span", "dev-badge", "DEV"));
  const mk = (label: string, to: string, cls = ""): HTMLButtonElement => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "dev-qbtn" + (cls ? ` ${cls}` : "");
    b.textContent = label;
    b.addEventListener("click", (e) => {
      e.stopPropagation();
      sendQuestDev(questId, to);
    });
    return b;
  };
  bar.appendChild(mk(status === "none" ? "Start" : "Restart", status === "none" ? "start" : "restart"));
  if (status !== "completed") bar.appendChild(mk("Complete", "complete", "dev-qbtn-done"));
  // Drop progress entirely (as if never taken) — for re-testing startQuest offers.
  if (status !== "none") bar.appendChild(mk("Unstart", "unstart", "dev-qbtn-unstart"));
  return bar;
}

function questsPage(): HTMLElement[] {
  if (questFocus) {
    const active = activeQuests().find((q) => q.spec.id === questFocus);
    if (active) return questDetailPage(active, false);
    const done = completedQuests().find((q) => q.spec.id === questFocus);
    if (done) return questDetailPage(done, true);
    // Unstarted / scrubbed while drilled in — drop back to overview.
    questFocus = null;
  }
  return questsOverviewPage();
}

function questsOverviewPage(): HTMLElement[] {
  const out: HTMLElement[] = [el("div", "sec-title", "Quests")];
  // Mirror the world tracker — clickable to open that quest's log.
  const wp = currentWaypoint();
  if (wp) {
    const echo = el("button", "tracker-echo") as HTMLButtonElement;
    echo.type = "button";
    echo.innerHTML =
      `<span class="te-k">Tracking</span>` +
      `<span class="te-title">${wp.questTitle}</span>` +
      `<span class="te-obj">${wp.desc}</span>` +
      `<span class="te-open">Open ›</span>`;
    echo.addEventListener("click", () => openQuest(wp.questId));
    out.push(echo);
  }

  const active = activeQuests();
  const done = completedQuests();

  if (!active.length && !done.length) {
    out.push(el("div", "empty", "No quests yet. Seek out those who need a sword."));
  }

  if (active.length) {
    if (done.length) out.push(el("div", "quest-group", "Underway"));
    const grid = el("div", "quest-overview");
    for (const v of active) grid.appendChild(questOverviewCard(v, false));
    out.push(grid);
  } else if (done.length) {
    out.push(el("div", "empty subtle", "Nothing underway. Finished deeds are below."));
  }

  if (done.length) {
    out.push(el("div", "quest-group", "Completed"));
    const grid = el("div", "quest-overview");
    for (const v of done) grid.appendChild(questOverviewCard(v, true));
    out.push(grid);
  }

  // Dev: every non-held quest with start/restart + step jumps.
  if (serverDevCommands) {
    out.push(el("div", "sec-title dev-sec", "Dev — all quests"));
    out.push(el("div", "dev-hint",
      "Open a quest card for step jumps. Restart rewinds to step 1. " +
      "Unstart drops the quest (as if never taken). " +
      "Also: /quest &lt;id&gt; restart|complete|unstart|&lt;stepId&gt;"));
    for (const q of QUESTS) {
      const isActive = active.some((v) => v.spec.id === q.id);
      if (isActive) continue; // already on a card above
      const isDone = done.some((v) => v.spec.id === q.id);
      const row = el("div", "dev-quest-row");
      row.appendChild(el("span", "dev-qname",
        `${q.title} <span class="dev-qid">${q.id}</span>` +
        (isDone ? ` <span class="dev-qstat">done</span>` : "")));
      row.appendChild(devQuestTools(q.id, isDone ? "completed" : "none"));
      const chips = el("div", "dev-chips");
      for (const s of q.steps) {
        const b = document.createElement("button");
        b.type = "button";
        b.className = "dev-qbtn";
        b.textContent = s.id;
        b.title = s.desc;
        b.addEventListener("click", () => sendQuestDev(q.id, s.id));
        chips.appendChild(b);
      }
      row.appendChild(chips);
      out.push(row);
    }
  }
  return out;
}

function questDetailPage(v: QuestView, done: boolean): HTMLElement[] {
  return [
    backButton("All quests", () => closeQuest()),
    questBlock(v, done),
  ];
}

// ---- Skills page (Plan 09) --------------------------------------------------
// Illuminated skill grimoire: each art is a card with XP bars + a prereq-depth
// tree per branch. Clicking a tree node opens a box-detail page (breadcrumb +
// Unlock when eligible). The ONLY spend intent is sendUnlockBox from that
// button — no optimistic mutation; the echoed skills/influence messages
// re-render via refreshJournalIfOpen.

/** Thousands-separated XP numbers ("3,140"). */
function fmt(n: number): string {
  return n.toLocaleString("en-US");
}

/** Short grant tags shown as chips on a box node. */
function grantTags(box: SkillBox): string[] {
  const g = box.grants;
  const parts: string[] = [];
  if (g.stat?.maxHp) parts.push(`+${g.stat.maxHp} HP`);
  if (g.stat?.damage) parts.push(`+${g.stat.damage}% dmg`);
  if (g.stat?.stamina) parts.push(`+${g.stat.stamina} sta`);
  if (g.stat?.armor) parts.push(`+${g.stat.armor}% armor`);
  if (g.weaponType) {
    const name = itemSpecOrNull(g.weaponType)?.name ?? g.weaponType;
    parts.push(`Wield ${name}`);
  }
  if (g.attack) parts.push(`Move · ${g.attack}`);
  // Prefer explicit grants.move; also list moves that require this box (authoritative gate).
  if (g.move) {
    const m = MOVES.find((x) => x.id === g.move);
    parts.push(`Move · ${m?.name ?? g.move}`);
  }
  for (const m of MOVES) {
    if (m.requiresBox === box.id && m.id !== g.move) parts.push(`Move · ${m.name}`);
  }
  if (g.fishing?.reelWindowMul) parts.push(`×${g.fishing.reelWindowMul} reel window`);
  if (g.fishing?.tableTier) parts.push(`+${g.fishing.tableTier} hook tier`);
  if (g.fishing?.netCount) parts.push(`+${g.fishing.netCount} nets`);
  if (g.fishing?.soakMul != null) parts.push(`${Math.round(g.fishing.soakMul * 100)}% soak`);
  if (g.hunting?.aimHitBonus) parts.push(`+${Math.round(g.hunting.aimHitBonus * 100)}% aim`);
  if (g.hunting?.trophyTier) parts.push(`+${g.hunting.trophyTier} trophy`);
  if (g.hunting?.trapCount) parts.push(`+${g.hunting.trapCount} traps`);
  if (g.hunting?.soakMul != null) parts.push(`${Math.round(g.hunting.soakMul * 100)}% soak`);
  if (g.cooking?.panWindowMul) parts.push(`×${g.cooking.panWindowMul} pan window`);
  if (g.cooking?.healBonus) parts.push(`+${g.cooking.healBonus} heal`);
  if (g.cooking?.brewCount) parts.push(`+${g.cooking.brewCount} pots`);
  if (g.cooking?.soakMul != null) parts.push(`${Math.round(g.cooking.soakMul * 100)}% soak`);
  return parts;
}

/** A box's display state. Eligibility comes from the SHARED `checkBox` rule (the same one
 *  the server enforces on unlockBox, so they can't drift); this only turns the failing
 *  gate into a human reason. */
function boxState(spec: SkillSpec, box: SkillBox, unlocked: string[], invested: number):
  { state: "unlocked" | "eligible" | "locked"; reason?: string } {
  const chk = checkBox(spec, box, {
    xp: (bar) => skillXp(spec.id, bar),
    hasBox: (id) => unlocked.includes(id),
    influence: influenceAmount(),
    standingRank: (track) => standingOf(track)?.rank ?? 0,
    invested,
  });
  if (chk.ok) return { state: "eligible" };
  if (chk.gate === "unlocked") return { state: "unlocked" };
  const reason =
    chk.gate === "xp" ? `Needs ${xpReqLabel(spec, chk.bar, chk.need)}`
    : chk.gate === "prereq" ? `Requires ${boxOf(spec, chk.prereq)?.name ?? chk.prereq}`
    : chk.gate === "rank" ? `Requires ${standingById(chk.track)?.name ?? chk.track} rank ${chk.rank}`
    : chk.gate === "influence" ? "Not enough influence"
    : "Skill cap reached";
  return { state: "locked", reason };
}

/** Human XP gate label — named rung when authored ("Soldier"), else "100 Melee XP". */
function xpReqLabel(spec: SkillSpec, bar: string, needXp: number): string {
  const name = rungNameForXp(spec, bar, needXp);
  if (name) return name;
  return `${fmt(needXp)} ${barLabel(bar)} XP`;
}

/** Depth of a box within a branch group (roots = 0). Prereqs outside the group
 *  don't count — each branch tree is self-contained for layout. */
function boxDepth(box: SkillBox, inGroup: Map<string, SkillBox>, memo: Map<string, number>): number {
  const hit = memo.get(box.id);
  if (hit !== undefined) return hit;
  memo.set(box.id, 0); // cycle guard
  const prereqs = (box.requires.prereq ?? []).filter((id) => inGroup.has(id));
  const d = prereqs.length
    ? 1 + Math.max(...prereqs.map((id) => boxDepth(inGroup.get(id)!, inGroup, memo)))
    : 0;
  memo.set(box.id, d);
  return d;
}

/** Shared progress snapshot for a skill (overview cards + detail header). */
function skillProgress(spec: SkillSpec): {
  unlocked: string[];
  invested: number;
  rank: number;
  ranks: { name: string }[];
  rankName: string;
  eligible: number;
} {
  const unlocked = skillBoxes(spec.id);
  const invested = investedIn(spec, unlocked);
  const track = standingById(spec.track);
  const rank = standingOf(spec.track)?.rank ?? 0;
  const ranks = track?.ranks ?? [];
  const rankName = ranks[rank]?.name ?? "Unproven";
  let eligible = 0;
  for (const box of spec.boxes) {
    if (boxState(spec, box, unlocked, invested).state === "eligible") eligible++;
  }
  return { unlocked, invested, rank, ranks, rankName, eligible };
}

/** Highest threshold index the player has reached (0-based). */
function rungIndex(cur: number, thresholds: readonly number[]): number {
  let i = 0;
  for (let j = 0; j < thresholds.length; j++) {
    if (cur >= thresholds[j]!) i = j;
  }
  return i;
}

/** Compact XP bars (shared by overview cards and detail).
 *  Pass `only` to show a single bar (active branch tab).
 *  With rung names: LEFT = current rank, RIGHT = working-toward, bar in middle.
 *  Full ladder under the bar shows past / current / upcoming. */
function xpBarsEl(spec: SkillSpec, only?: string | null): HTMLElement {
  const bars = el("div", "xp-bars");
  // Prefer the active branch's matching XP bar; fall back to all if no match.
  const keys = only && spec.xpBars.includes(only) ? [only] : spec.xpBars;
  for (const bar of keys) {
    const cur = skillXp(spec.id, bar);
    const thresholds = spec.thresholds?.[bar] ?? [];
    const names = spec.rungNames?.[bar] ?? [];
    const next = thresholds.find((t) => t > 0 && cur < t);
    const nextIdx = next != null ? thresholds.indexOf(next) : -1;
    const at = rungIndex(cur, thresholds);
    const prevXp = thresholds[at] ?? 0;
    const curName = names[at];
    const nextName = nextIdx >= 0 ? names[nextIdx] : undefined;
    const hasNames = names.length > 0 && thresholds.length > 0;

    // Progress fill: toward the next rung when named; else career total.
    let fillFrac: number;
    if (next != null && next > prevXp) {
      fillFrac = Math.min(1, Math.max(0, (cur - prevXp) / (next - prevXp)));
    } else if (next != null && next > 0) {
      fillFrac = Math.min(1, cur / next);
    } else {
      const maxT = thresholds.length ? thresholds[thresholds.length - 1]! : 0;
      fillFrac = maxT > 0 ? Math.min(1, cur / maxT) : 1;
    }

    const block = el("div", "xpbar-block");
    const row = el("div", hasNames ? "xpbar named" : "xpbar");

    // LEFT: who you are now (rung name) — or the bar label when unnamed.
    if (hasNames) {
      const left = el("span", "xp-end current", curName ?? barLabel(bar));
      left.title = curName
        ? `You are · ${curName} (${fmt(cur)} XP)`
        : barLabel(bar);
      row.appendChild(left);
    } else {
      row.appendChild(el("span", "bar-name", barLabel(bar)));
    }

    // CENTER: track + optional numeric progress under it.
    const mid = el("span", "xp-mid");
    const trackEl = el("span", "bar");
    const maxT = thresholds.length ? thresholds[thresholds.length - 1]! : 0;
    if (maxT > 0 && !hasNames) {
      // Unnamed bars keep intermediate ticks for orientation.
      thresholds.forEach((t) => {
        if (t <= 0 || t >= maxT) return;
        const tick = el("span", "tick");
        tick.style.left = `${(t / maxT) * 100}%`;
        tick.title = `${fmt(t)} XP`;
        tick.style.pointerEvents = "auto";
        trackEl.appendChild(tick);
      });
    }
    const fill = el("span", "fill");
    fill.style.width = `${(fillFrac * 100).toFixed(1)}%`;
    trackEl.appendChild(fill);
    mid.appendChild(trackEl);
    const num = el(
      "span",
      "num",
      next != null ? `${fmt(cur)} / ${fmt(next)}` : `${fmt(cur)} · MAX`,
    );
    mid.appendChild(num);
    row.appendChild(mid);

    // RIGHT: what you're working toward (next rung) — only when named.
    if (hasNames) {
      const right = el(
        "span",
        `xp-end target${next == null ? " maxed" : ""}`,
        next != null ? (nextName ?? fmt(next)) : "MAX",
      );
      right.title = next != null
        ? `Working toward · ${nextName ?? fmt(next)} (${fmt(next)} XP)`
        : `Peak · ${curName ?? "MAX"}`;
      row.appendChild(right);
    }

    block.appendChild(row);

    // Full ladder: past / current / upcoming for every named rung.
    if (hasNames) {
      const ladder = el("div", "rung-ladder");
      ladder.setAttribute("aria-label", `${barLabel(bar)} ranks`);
      thresholds.forEach((t, i) => {
        const name = names[i];
        if (!name) return;
        const isPast = i < at;
        const isCurrent = i === at;
        const isNext = next != null && i === nextIdx;
        const state = isCurrent ? "current" : isPast ? "past" : isNext ? "next" : "upcoming";
        const chip = el("span", `rung ${state}`, name);
        chip.title =
          isPast ? `Was · ${name} (${fmt(t)} XP)`
          : isCurrent ? `You are · ${name} (${fmt(t)} XP)`
          : isNext ? `Next · ${name} (${fmt(t)} XP)`
          : `Upcoming · ${name} (${fmt(t)} XP)`;
        if (isCurrent) chip.setAttribute("aria-current", "true");
        ladder.appendChild(chip);
      });
      block.appendChild(ladder);
    }
    bars.appendChild(block);
  }
  return bars;
}

function masteryEl(invested: number, cap: number): HTMLElement {
  const mastery = el("div", "skill-mastery");
  mastery.innerHTML =
    `<span class="mastery-label">Mastery</span>` +
    `<span class="mastery-pips" aria-label="${invested} of ${cap}">` +
    Array.from({ length: cap }, (_, i) =>
      `<span class="mp${i < invested ? " filled" : ""}"></span>`).join("") +
    `</span>` +
    `<span class="mastery-num">${invested}/${cap}</span>`;
  return mastery;
}

/** Overview tile — click to open the skill tree. */
function skillOverviewCard(spec: SkillSpec): HTMLElement {
  const { invested, rankName, eligible } = skillProgress(spec);
  const card = el("button", `skill-card sk-${spec.id}`) as HTMLButtonElement;
  card.type = "button";

  const top = el("div", "card-top");
  top.appendChild(makeSkillCrestEl(spec.id));
  const identity = el("div", "skill-identity");
  identity.appendChild(el("h3", undefined, spec.name));
  identity.appendChild(el("span", "rank-name", rankName));
  top.appendChild(identity);
  top.appendChild(el("span", "card-chev", "›"));
  card.appendChild(top);

  card.appendChild(xpBarsEl(spec));

  const meta = el("div", "card-meta");
  meta.appendChild(masteryEl(invested, spec.capBoxes));
  if (eligible > 0) {
    meta.appendChild(el("span", "card-eligible",
      `${eligible} ready to unlock`));
  }
  card.appendChild(meta);
  card.appendChild(el("div", "card-hint", "Open skill tree ›"));

  card.addEventListener("click", () => openSkill(spec.id));
  return card;
}

/** Ordered branch keys that actually have boxes (authored order first). */
function skillBranchKeys(spec: SkillSpec): string[] {
  const groups = new Map<string, number>();
  for (const b of spec.boxes) {
    const key = b.branch ?? "";
    groups.set(key, (groups.get(key) ?? 0) + 1);
  }
  const order = [...(spec.branches ?? [])];
  for (const k of groups.keys()) if (!order.includes(k)) order.push(k);
  return order.filter((k) => (groups.get(k) ?? 0) > 0);
}

/** Full-page skill art for one branch (or the whole skill if single-branch). */
function skillBlock(spec: SkillSpec, branchKey: string | null): HTMLElement {
  const wrap = el("div", `skill sk-${spec.id} skill-full`);
  const { unlocked, invested } = skillProgress(spec);

  // --- Header ---------------------------------------------------------------
  const head = el("div", "skill-head");
  // Detail header: painted skill plate (branch emoji is only for tab chips).
  head.appendChild(makeSkillCrestEl(spec.id));

  const identity = el("div", "skill-identity");
  const title = branchKey
    ? `${spec.name} · ${barLabel(branchKey)}`
    : spec.name;
  identity.appendChild(el("h3", undefined, title));
  // Standing track lives on About Me / deeds — not a second pill row here.
  // XP rungs under the bar are the skill's named progression surface.
  head.appendChild(identity);
  head.appendChild(masteryEl(invested, spec.capBoxes));
  wrap.appendChild(head);

  // XP for the active branch when it maps to a bar; else all bars.
  const xpOnly = branchKey && spec.xpBars.includes(branchKey) ? branchKey : null;
  wrap.appendChild(xpBarsEl(spec, xpOnly));

  // --- Active branch tree (full width) ------------------------------------
  const list = spec.boxes.filter((b) => (b.branch ?? "") === (branchKey ?? ""));
  const treeList = list.length ? list : spec.boxes; // fallback: show everything
  const branches = el("div", "branches full");
  // Multiple roots ⇒ distinct path columns (Defense / Balanced / DPS).
  // One root ⇒ classic single climb.
  branches.appendChild(pathForest(spec, branchKey ?? "", treeList, unlocked, invested));
  wrap.appendChild(branches);
  return wrap;
}

/** Playstyle labels for path columns (keyed by root box id). Falls back to the root name. */
/** Root box id → path column title. Required for multi-root branches. */
const PATH_LABELS: Record<string, string> = {
  guard: "Defense",
  wind: "Balanced",
  might: "DPS",
};

/** Walk prereqs inside the group until a root; true if that root is `rootId`. */
function boxBelongsToRoot(
  box: SkillBox,
  rootId: string,
  inGroup: Map<string, SkillBox>,
): boolean {
  let cur: SkillBox | undefined = box;
  const seen = new Set<string>();
  while (cur) {
    if (cur.id === rootId) return true;
    if (seen.has(cur.id)) return false;
    seen.add(cur.id);
    const parents = (cur.requires.prereq ?? [])
      .map((id) => inGroup.get(id))
      .filter((b): b is SkillBox => !!b);
    if (!parents.length) return cur.id === rootId;
    // Multi-prereq rare; first in-group parent defines the path for layout.
    cur = parents[0];
  }
  return false;
}

/** Forest of path columns when a branch has several independent roots; else one tree. */
function pathForest(
  spec: SkillSpec,
  key: string,
  list: SkillBox[],
  unlocked: string[],
  invested: number,
): HTMLElement {
  const inGroup = new Map(list.map((b) => [b.id, b] as const));
  const roots = list
    .filter((b) => !(b.requires.prereq ?? []).some((id) => inGroup.has(id)))
    .sort((a, b) => list.indexOf(a) - list.indexOf(b));

  if (roots.length <= 1) {
    return branchTree(spec, key, list, unlocked, invested, true);
  }

  const forest = el("div", "path-forest");
  forest.setAttribute("role", "group");
  forest.setAttribute("aria-label", "Skill paths");

  for (const root of roots) {
    const members = list.filter((b) => boxBelongsToRoot(b, root.id, inGroup));
    const col = el("div", "path-col");
    col.dataset.path = root.id;

    const title = el("div", "path-title");
    const mark = el("span", "path-mark");
    mark.setAttribute("aria-hidden", "true");
    title.append(mark, el("span", "path-name", PATH_LABELS[root.id] ?? root.name));
    // Compact progress for this path alone.
    const done = members.filter((b) => unlocked.includes(b.id)).length;
    title.appendChild(el("span", "path-count", `${done}/${members.length}`));
    col.appendChild(title);

    col.appendChild(branchTree(spec, key, members, unlocked, invested, true));
    forest.appendChild(col);
  }
  return forest;
}

/** Beautiful top tab strip for multi-branch skills (Melee / Ranged, …). */
function branchTabsEl(spec: SkillSpec, keys: string[], active: string): HTMLElement {
  const nav = el("div", "branch-tabs");
  nav.setAttribute("role", "tablist");
  nav.setAttribute("aria-label", `${spec.name} paths`);

  for (const key of keys) {
    const unlocked = skillBoxes(spec.id);
    const invested = investedIn(spec, unlocked);
    const branchBoxes = spec.boxes.filter((b) => (b.branch ?? "") === key);
    let eligible = 0;
    let done = 0;
    for (const box of branchBoxes) {
      const st = boxState(spec, box, unlocked, invested);
      if (st.state === "eligible") eligible++;
      if (st.state === "unlocked") done++;
    }

    const tab = el("button", `branch-tab${key === active ? " sel" : ""}`) as HTMLButtonElement;
    tab.type = "button";
    tab.setAttribute("role", "tab");
    tab.setAttribute("aria-selected", key === active ? "true" : "false");
    tab.dataset.branch = key;

    const crest = makeBranchCrestEl(key);
    const label = el("span", "bt-label", barLabel(key));
    const meta = el("span", "bt-meta", `${done}/${branchBoxes.length}`);
    tab.append(crest, label, meta);
    if (eligible > 0) {
      const badge = el("span", "bt-ready", String(eligible));
      badge.title = `${eligible} ready to unlock`;
      tab.appendChild(badge);
    }

    tab.addEventListener("click", () => {
      if (key !== skillBranch) setSkillBranch(key);
    });
    nav.appendChild(tab);
  }
  return nav;
}

/** One branch as a bottom-up skill tree: roots at the bottom, advanced boxes
 *  above — each tier builds on the prereqs below it (Steady Hands → Deep Waters). */
function branchTree(
  spec: SkillSpec,
  key: string,
  list: SkillBox[],
  unlocked: string[],
  invested: number,
  hideTitle = false,
): HTMLElement {
  const branch = el("div", "branch");
  if (key && !hideTitle) {
    const title = el("div", "branch-name");
    title.innerHTML = `<span class="bn-mark"></span><span>${barLabel(key)}</span>`;
    branch.appendChild(title);
  }

  const inGroup = new Map(list.map((b) => [b.id, b] as const));
  const memo = new Map<string, number>();
  const byDepth = new Map<number, SkillBox[]>();
  let maxD = 0;
  for (const box of list) {
    const d = boxDepth(box, inGroup, memo);
    maxD = Math.max(maxD, d);
    let tier = byDepth.get(d);
    if (!tier) byDepth.set(d, (tier = []));
    tier.push(box);
  }

  // Stable left→right order within a tier: content order, then name.
  const orderOf = (b: SkillBox) => list.indexOf(b);
  for (const tier of byDepth.values()) {
    tier.sort((a, b) => orderOf(a) - orderOf(b) || a.name.localeCompare(b.name));
  }

  const tree = el("div", "tree");
  tree.style.setProperty("--tiers", String(maxD + 1));

  // Advanced (max depth) at the TOP → roots (depth 0) at the BOTTOM.
  for (let d = maxD; d >= 0; d--) {
    const tierBoxes = byDepth.get(d) ?? [];
    const row = el("div", "tree-row");
    row.dataset.depth = String(d);
    if (d === 0) row.classList.add("roots");
    if (d === maxD) row.classList.add("apex");

    for (const box of tierBoxes) {
      const cell = el("div", "tree-cell");
      cell.dataset.box = box.id;

      // Stem hanging below advanced nodes, pointing down toward their prereqs.
      if (d > 0) {
        const parents = (box.requires.prereq ?? []).filter((id) => inGroup.has(id));
        const parentNames = parents
          .map((id) => inGroup.get(id)?.name ?? id)
          .join(", ");
        const stem = el("div", "tree-stem");
        const childOn = unlocked.includes(box.id);
        const parentOn = parents.some((id) => unlocked.includes(id));
        if (childOn) stem.classList.add("lit");
        else if (parentOn) stem.classList.add("ready");
        if (parentNames) stem.title = `Builds on ${parentNames}`;
        // Structure: [card] then [stem hanging down] — stem is after the card
        // in DOM so it sits under the node visually (column flex).
        cell.appendChild(boxNode(spec, box, unlocked, invested));
        cell.appendChild(stem);
        if (parentNames) {
          // Parent tier sits below (roots at the bottom); label points down.
          const via = el("div", "tree-via", `from ${parentNames}`);
          if (childOn) via.classList.add("lit");
          cell.appendChild(via);
        }
      } else {
        // Root: small ground flourish under the foundation box.
        cell.appendChild(boxNode(spec, box, unlocked, invested));
        cell.appendChild(el("div", "tree-root"));
      }
      row.appendChild(cell);
    }
    tree.appendChild(row);

    // Horizontal rail under this tier (except under the root tier) so siblings
    // of the same depth read as a shared rank on the climb.
    if (d > 0) {
      const rail = el("div", "tree-rail");
      // Light the rail when any node on this tier is unlocked or eligible.
      const anyLit = tierBoxes.some((b) => {
        const st = boxState(spec, b, unlocked, invested);
        return st.state === "unlocked" || st.state === "eligible";
      });
      if (anyLit) rail.classList.add("lit");
      tree.appendChild(rail);
    }
  }

  branch.appendChild(tree);
  return branch;
}

/** One tree node. Every node is clickable and opens the box-detail page;
 *  spending only happens from the detail's Unlock button. */
function boxNode(spec: SkillSpec, box: SkillBox, unlocked: string[], invested: number): HTMLElement {
  const st = boxState(spec, box, unlocked, invested);
  const eligible = st.state === "eligible";
  const node = el("button", `box ${st.state}`) as HTMLButtonElement;
  node.type = "button";

  const seal = el("span", "box-seal");
  seal.setAttribute("aria-hidden", "true");
  seal.textContent = st.state === "unlocked" ? "✦" : eligible ? "◎" : "·";

  const body = el("span", "box-body");
  body.appendChild(el("span", "box-name", box.name));

  const tags = grantTags(box);
  if (tags.length) {
    const grants = el("span", "box-grants");
    for (const t of tags) grants.appendChild(el("span", "tag", t));
    body.appendChild(grants);
  }

  if (st.state === "locked" && st.reason) {
    body.appendChild(el("span", "box-gate", st.reason));
  } else if (eligible) {
    body.appendChild(el("span", "box-cost", `Spend ${box.cost}◈`));
  } else {
    body.appendChild(el("span", "box-done", "Unlocked"));
  }

  node.append(seal, body);
  node.title = st.state === "unlocked"
    ? `${box.name} — unlocked · view details`
    : eligible
      ? `${box.name} — ready to unlock · view details`
      : `${box.name} — ${st.reason ?? "Locked"} · view details`;
  node.addEventListener("click", () => openSkillBox(box.id));
  return node;
}

/** Breadcrumb trail: clickable ancestors + the current leaf. */
function breadcrumb(parts: { label: string; onClick?: () => void }[]): HTMLElement {
  const nav = el("nav", "journal-bc");
  nav.setAttribute("aria-label", "Breadcrumb");
  parts.forEach((p, i) => {
    if (i > 0) nav.appendChild(el("span", "bc-sep", "›"));
    if (p.onClick) {
      const b = el("button", "bc-link") as HTMLButtonElement;
      b.type = "button";
      b.textContent = p.label;
      b.addEventListener("click", p.onClick);
      nav.appendChild(b);
    } else {
      const cur = el("span", "bc-cur", p.label);
      cur.setAttribute("aria-current", "page");
      nav.appendChild(cur);
    }
  });
  return nav;
}

/** Full-page box detail: grants, gates, and Unlock when eligible. */
function skillBoxDetailPage(spec: SkillSpec, box: SkillBox): HTMLElement[] {
  const { unlocked, invested } = skillProgress(spec);
  const st = boxState(spec, box, unlocked, invested);
  const branch = box.branch ?? skillBranch;
  const tags = grantTags(box);

  const crumbs: { label: string; onClick?: () => void }[] = [
    { label: "Skills", onClick: () => closeSkill() },
    { label: spec.name, onClick: () => closeSkillBox() },
  ];
  if (branch) {
    crumbs.push({
      label: barLabel(branch),
      onClick: () => {
        // Land on this box's branch tree, not a different tab.
        skillBranch = branch;
        closeSkillBox();
      },
    });
  }
  crumbs.push({ label: box.name });

  // Top-left back control (breadcrumb is secondary orientation).
  const backLabel = branch
    ? `${barLabel(branch)} tree`
    : `${spec.name} tree`;
  const out: HTMLElement[] = [
    backButton(backLabel, () => {
      if (branch) skillBranch = branch;
      closeSkillBox();
    }),
    breadcrumb(crumbs),
  ];

  const page = el("div", `box-detail st-${st.state}`);

  // Header: seal + name + state chip
  const head = el("div", "bd-head");
  const seal = el("div", "bd-seal");
  seal.setAttribute("aria-hidden", "true");
  seal.textContent = st.state === "unlocked" ? "✦" : st.state === "eligible" ? "◎" : "·";
  head.appendChild(seal);
  const id = el("div", "bd-identity");
  id.appendChild(el("div", "bd-kicker",
    branch ? `${spec.name} · ${barLabel(branch)}` : spec.name));
  id.appendChild(el("h2", "bd-name", box.name));
  const chip =
    st.state === "unlocked" ? el("span", "bd-chip unlocked", "Unlocked")
    : st.state === "eligible" ? el("span", "bd-chip eligible", "Ready to unlock")
    : el("span", "bd-chip locked", "Locked");
  id.appendChild(chip);
  head.appendChild(id);
  page.appendChild(head);

  // Grants
  const grantsSec = el("section", "bd-section");
  grantsSec.appendChild(el("h3", "bd-sec-title", "Grants"));
  if (tags.length) {
    const list = el("ul", "bd-grants");
    for (const t of tags) list.appendChild(el("li", undefined, t));
    grantsSec.appendChild(list);
  } else {
    grantsSec.appendChild(el("div", "bd-empty", "No direct grants — a step on the path."));
  }
  page.appendChild(grantsSec);

  // Requirements
  const reqSec = el("section", "bd-section");
  reqSec.appendChild(el("h3", "bd-sec-title", "Requirements"));
  const reqList = el("ul", "bd-reqs");
  const bar = box.requires.bar;
  const needXp = box.requires.xp;
  const haveXp = skillXp(spec.id, bar);
  const xpMet = haveXp >= needXp;
  const needLabel = xpReqLabel(spec, bar, needXp);
  const haveName = rungNameForXp(spec, bar, haveXp);
  let haveNote = "";
  if (needXp > 0 && !xpMet) {
    // Named: "you: Soldier" — otherwise raw XP earned.
    haveNote = haveName
      ? ` <span class="rq-have">(you: ${haveName})</span>`
      : ` <span class="rq-have">(${fmt(haveXp)} earned)</span>`;
  }
  reqList.appendChild(el("li", xpMet ? "met" : "need", needLabel + haveNote));
  for (const pid of box.requires.prereq ?? []) {
    const pre = boxOf(spec, pid);
    const met = unlocked.includes(pid);
    reqList.appendChild(el("li", met ? "met" : "need",
      `Unlock ${pre?.name ?? pid}`));
  }
  if (box.requires.rank) {
    const track = standingById(box.requires.rank.track);
    const rankName = track?.ranks[box.requires.rank.rank]?.name
      ?? `rank ${box.requires.rank.rank}`;
    const met = (standingOf(box.requires.rank.track)?.rank ?? 0) >= box.requires.rank.rank;
    reqList.appendChild(el("li", met ? "met" : "need",
      `${track?.name ?? box.requires.rank.track}: ${rankName}`));
  }
  const costMet = influenceAmount() >= box.cost;
  reqList.appendChild(el("li", costMet || st.state === "unlocked" ? "met" : "need",
    `${box.cost}◈ influence` +
    (st.state !== "unlocked"
      ? ` <span class="rq-have">(${influenceAmount()} available)</span>`
      : "")));
  reqSec.appendChild(reqList);
  if (st.state === "locked" && st.reason) {
    reqSec.appendChild(el("div", "bd-gate", st.reason));
  }
  page.appendChild(reqSec);

  out.push(page);

  // Bottom action: Unlock only when eligible
  if (st.state === "eligible") {
    const foot = el("div", "bd-actions");
    const btn = el("button", "bd-unlock") as HTMLButtonElement;
    btn.type = "button";
    btn.innerHTML =
      `<span class="bu-label">Unlock</span>` +
      `<span class="bu-cost"><span class="inf-gem">◈</span> ${box.cost}</span>`;
    btn.title = `Spend ${box.cost}◈ to unlock ${box.name}`;
    btn.addEventListener("click", () => sendUnlockBox(spec.id, box.id));
    foot.appendChild(btn);
    out.push(foot);
  } else if (st.state === "unlocked") {
    out.push(el("div", "bd-actions done",
      `<span class="bd-unlocked-note">Already unlocked</span>`));
  } else {
    out.push(el("div", "bd-actions locked",
      `<span class="bd-locked-note">${st.reason ?? "Not yet eligible"}</span>`));
  }

  return out;
}

function skillsHero(sub: string): HTMLElement {
  const head = el("div", "skills-hero");
  head.innerHTML =
    `<div class="skills-hero-copy">` +
      `<div class="skills-kicker">The Arts</div>` +
      `<div class="sec-title skills-title">Skills</div>` +
      `<div class="skills-sub">${sub}</div>` +
    `</div>` +
    `<div class="influence-badge" title="Global influence pool">` +
      `<span class="inf-gem">◈</span>` +
      `<span class="inf-num">${influenceAmount()}</span>` +
      `<span class="inf-cap">of ${PROGRESSION_DEFAULTS.globalCap}</span>` +
      `<span class="inf-label">Influence</span>` +
    `</div>`;
  return head;
}

/** Skills section: overview → skill tree → optional box detail. */
function skillsPage(): HTMLElement[] {
  if (skillFocus) {
    const spec = SKILLS.find((s) => s.id === skillFocus);
    if (!spec) {
      skillFocus = null;
      skillBoxFocus = null;
      return skillsOverviewPage();
    }
    if (skillBoxFocus) {
      const box = boxOf(spec, skillBoxFocus);
      if (box) return skillBoxDetailPage(spec, box);
      skillBoxFocus = null; // stale box id
    }
    return skillTreePage(spec);
  }
  return skillsOverviewPage();
}

function skillsOverviewPage(): HTMLElement[] {
  const out: HTMLElement[] = [
    skillsHero("Choose an art to open its skill tree. Spend influence on ready boxes."),
  ];
  const title = equippedTitle();
  if (title) {
    out.push(el("div", "known-as",
      `<span class="ka-mark">❧</span> Known as <em>“${title}”</em>`));
  }
  if (!SKILLS.length) {
    out.push(el("div", "empty", "No arts to study yet. The masters keep their own counsel."));
    return out;
  }
  const grid = el("div", "skill-overview");
  for (const spec of SKILLS) grid.appendChild(skillOverviewCard(spec));
  out.push(grid);
  return out;
}

function skillTreePage(spec: SkillSpec): HTMLElement[] {
  const branchKeys = skillBranchKeys(spec);
  // Keep the active tab valid after content changes / stale state.
  if (branchKeys.length) {
    if (!skillBranch || !branchKeys.includes(skillBranch)) {
      skillBranch = branchKeys[0]!;
    }
  } else {
    skillBranch = null;
  }

  const out: HTMLElement[] = [
    backButton("All skills", () => closeSkill()),
    breadcrumb([
      { label: "Skills", onClick: () => closeSkill() },
      { label: spec.name },
    ]),
  ];

  // Compact hero + influence (no long subcopy — the tree owns the page).
  const hero = el("div", "skills-hero compact");
  hero.innerHTML =
    `<div class="skills-hero-copy">` +
      `<div class="skills-kicker">${spec.name}</div>` +
      `<div class="sec-title skills-title">${skillBranch ? barLabel(skillBranch) : "Skill tree"}</div>` +
    `</div>` +
    `<div class="influence-badge" title="Global influence pool">` +
      `<span class="inf-gem">◈</span>` +
      `<span class="inf-num">${influenceAmount()}</span>` +
      `<span class="inf-cap">of ${PROGRESSION_DEFAULTS.globalCap}</span>` +
      `<span class="inf-label">Influence</span>` +
    `</div>`;
  out.push(hero);

  if (branchKeys.length > 1 && skillBranch) {
    out.push(branchTabsEl(spec, branchKeys, skillBranch));
  }

  out.push(skillBlock(spec, skillBranch));
  out.push(el("div", "skill-legend",
    `<span class="lg unlocked"><i>✦</i> Unlocked</span>` +
    `<span class="lg eligible"><i>◎</i> Ready — open to unlock</span>` +
    `<span class="lg locked"><i>○</i> Locked</span>`));
  return out;
}

// ---- About Me ---------------------------------------------------------------
// Character-forward dossier: live 3D showcase + interactive palette, vitals,
// kit, and renown. The showcase reuses the real player builder so what you see
// here is what the world sees (look + gear + held kit).

const ABOUT_PALETTE: { key: "shirt" | "pants" | "skin" | "hair" | "eye"; label: string }[] = [
  { key: "shirt", label: "Shirt" },
  { key: "pants", label: "Trousers" },
  { key: "skin", label: "Skin" },
  { key: "hair", label: "Hair" },
  { key: "eye", label: "Eyes" },
];

const SLOT_LABEL: Record<WearSlot, string> = {
  head: "Head", torso: "Torso", legs: "Legs", feet: "Feet", back: "Back",
};
const SLOT_GLYPH: Record<WearSlot, string> = {
  head: "🎩", torso: "👕", legs: "👖", feet: "🥾", back: "🎒",
};

function prettyStyle(s: string): string {
  if (s === "none") return "None";
  return s.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function aboutPage(): HTMLElement[] {
  const name = me.name || "Adventurer";
  const title = equippedTitle();
  const look = me.look;
  const gear = wornGear();
  const wt = equippedType();
  const shieldT = equippedShieldType();
  const toolT = equippedTool();
  const weaponName = wt ? itemSpec(wt).name : "Bare fists";
  const weaponIcon = wt ? itemSpec(wt).icon : "✊";
  const hp = Math.max(0, game.hp);
  const sta = Math.max(0, game.stamina);
  const hpPct = game.maxHp > 0 ? Math.min(100, (hp / game.maxHp) * 100) : 0;
  const staPct = game.maxStamina > 0 ? Math.min(100, (sta / game.maxStamina) * 100) : 0;

  // ---- Hero: portrait + identity ------------------------------------------
  const hero = el("div", "about-hero");

  const stage = el("div", "about-stage");
  const frame = el("div", "about-frame");
  const host = el("div", "about-avatar");
  frame.appendChild(host);
  const spinHint = el("div", "about-spin-hint", "Drag to turn · double-click to face");
  stage.append(frame, spinHint);
  aboutAvatar = mountAboutAvatar(host);

  const identity = el("div", "about-identity");
  identity.appendChild(el("div", "about-kicker", "Adventurer’s dossier"));
  identity.appendChild(el("h2", "about-name", name));
  if (title) {
    identity.appendChild(el("div", "about-title",
      `<span class="at-mark">❧</span> Known as <em>“${title}”</em>`));
  } else {
    identity.appendChild(el("div", "about-title quiet",
      `<span class="at-mark">❧</span> A wanderer of no particular renown`));
  }

  const traits = el("div", "about-traits");
  traits.innerHTML =
    `<span class="trait">${look.sex === "female" ? "♀ Female" : "♂ Male"}</span>` +
    `<span class="trait">Hair · ${prettyStyle(look.hairStyle)}</span>` +
    (look.beardStyle !== "none"
      ? `<span class="trait">Beard · ${prettyStyle(look.beardStyle)}</span>`
      : "");
  identity.appendChild(traits);

  // Interactive palette — click a chip to spotlight that color.
  const palette = el("div", "about-palette");
  const palCaption = el("div", "about-pal-caption", "Your colors — click a swatch");
  let selectedKey: string | null = null;
  for (const { key, label } of ABOUT_PALETTE) {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "about-chip";
    chip.dataset.key = key;
    chip.title = `${label} · ${toCssHex(look[key])}`;
    chip.innerHTML =
      `<span class="sw" style="background:${toCssHex(look[key])}"></span>` +
      `<span class="lb">${label}</span>`;
    chip.addEventListener("click", () => {
      const next = selectedKey === key ? null : key;
      selectedKey = next;
      for (const c of palette.querySelectorAll(".about-chip")) {
        c.classList.toggle("sel", (c as HTMLElement).dataset.key === next);
      }
      if (next) {
        const entry = ABOUT_PALETTE.find((p) => p.key === next)!;
        palCaption.textContent = `${entry.label} · ${toCssHex(look[next as keyof typeof look] as number)}`;
        palCaption.classList.add("lit");
        host.classList.add("focus-glow");
      } else {
        palCaption.textContent = "Your colors — click a swatch";
        palCaption.classList.remove("lit");
        host.classList.remove("focus-glow");
      }
    });
    palette.appendChild(chip);
  }
  identity.append(palette, palCaption);

  hero.append(stage, identity);

  // ---- Vitals + kit -------------------------------------------------------
  const grid = el("div", "about-grid");

  const vitals = el("div", "about-card about-vitals");
  vitals.appendChild(el("div", "about-card-title", "Vitals"));
  vitals.appendChild(vitalBar("Hitpoints", hp, game.maxHp, hpPct, "hp"));
  vitals.appendChild(vitalBar("Stamina", sta, game.maxStamina, staPct, "sta"));
  grid.appendChild(vitals);

  const kit = el("div", "about-card about-kit");
  kit.appendChild(el("div", "about-card-title", "Kit"));

  // Held weapon / tool row — clickable for a little focus pulse.
  const held = el("div", "about-held");
  const mainHand = aboutHeldTile(
    "Main hand",
    weaponIcon,
    weaponName,
    wt ? "weapon" : "fists",
  );
  held.appendChild(mainHand);
  if (shieldT) {
    const s = itemSpec(shieldT);
    held.appendChild(aboutHeldTile("Off hand", s.icon, s.name, "shield"));
  } else if (toolT && toolT !== wt) {
    const t = itemSpec(toolT);
    held.appendChild(aboutHeldTile("Tool", t.icon, t.name, "tool"));
  } else {
    held.appendChild(aboutHeldTile("Off hand", "—", "Empty", "empty"));
  }
  kit.appendChild(held);

  const gearRow = el("div", "about-gear");
  for (const slot of WEAR_SLOTS) {
    const type = gear[slot];
    const cell = document.createElement("button");
    cell.type = "button";
    cell.className = "about-gear-cell" + (type ? " worn" : " empty");
    if (type) {
      const spec = itemSpec(type);
      cell.innerHTML =
        `<span class="g-icon">${spec.icon}</span>` +
        `<span class="g-slot">${SLOT_LABEL[slot]}</span>` +
        `<span class="g-name">${spec.name}</span>`;
      cell.title = `${SLOT_LABEL[slot]} · ${spec.name}`;
    } else {
      cell.innerHTML =
        `<span class="g-icon dim">${SLOT_GLYPH[slot]}</span>` +
        `<span class="g-slot">${SLOT_LABEL[slot]}</span>` +
        `<span class="g-name dim">Empty</span>`;
      cell.title = `${SLOT_LABEL[slot]} · empty`;
    }
    cell.addEventListener("click", () => {
      for (const c of gearRow.querySelectorAll(".about-gear-cell")) c.classList.remove("sel");
      if (type) cell.classList.toggle("sel");
    });
    gearRow.appendChild(cell);
  }
  kit.appendChild(gearRow);
  grid.appendChild(kit);

  // ---- Renown tally -------------------------------------------------------
  const renown = el("div", "about-renown");
  renown.appendChild(el("div", "about-card-title", "Renown"));
  const stats = el("div", "about-stats");
  const done = completedQuests().length;
  const active = activeQuests().length;
  const questLabel = active > 0 ? `${done} done · ${active} active` : `${done} completed`;
  for (const s of [
    { k: "Foes slain", v: String(killCount()), icon: "⚔" },
    { k: "Obol", v: String(walletObol()), icon: "◎" },
    { k: "Influence", v: String(influenceAmount()), icon: "◈" },
    { k: "Quests", v: questLabel, icon: "📜" },
  ]) {
    const tile = document.createElement("button");
    tile.type = "button";
    tile.className = "about-stat";
    tile.innerHTML =
      `<span class="as-icon">${s.icon}</span>` +
      `<span class="as-val">${s.v}</span>` +
      `<span class="as-key">${s.k}</span>`;
    tile.addEventListener("click", () => {
      for (const t of stats.querySelectorAll(".about-stat")) t.classList.remove("sel");
      tile.classList.add("sel");
    });
    stats.appendChild(tile);
  }
  renown.appendChild(stats);

  // Standing tracks the player has touched.
  const standingBits: HTMLElement[] = [];
  for (const track of STANDINGS) {
    const st = standingOf(track.id);
    if (!st || (st.rank <= 0 && st.progress <= 0)) continue;
    const ranks = track.ranks;
    const rankName = ranks[Math.max(0, st.rank - 1)]?.name
      ?? (st.rank > 0 ? `Rank ${st.rank}` : "Unranked");
    const next = ranks[st.rank];
    const prevNeed = ranks[st.rank - 1]?.progress ?? 0;
    const nextNeed = next?.progress ?? prevNeed;
    const span = Math.max(1, nextNeed - prevNeed);
    const pct = next
      ? Math.min(100, Math.max(0, ((st.progress - prevNeed) / span) * 100))
      : 100;
    const row = el("div", "about-standing");
    row.innerHTML =
      `<div class="as-head">` +
        `<span class="as-track">${track.name}</span>` +
        `<span class="as-rank">${rankName}</span>` +
      `</div>` +
      `<div class="as-bar"><div class="fill" style="width:${pct.toFixed(1)}%"></div></div>` +
      (next
        ? `<div class="as-next">${st.progress} / ${nextNeed} toward ${next.name}</div>`
        : `<div class="as-next">Highest renown</div>`);
    standingBits.push(row);
  }
  if (standingBits.length) {
    const standWrap = el("div", "about-standings");
    standWrap.append(...standingBits);
    renown.appendChild(standWrap);
  }

  return [hero, grid, renown];
}

function vitalBar(label: string, cur: number, max: number, pct: number, kind: "hp" | "sta"): HTMLElement {
  const row = el("div", `about-vital ${kind}`);
  row.innerHTML =
    `<div class="av-head">` +
      `<span class="av-label">${label}</span>` +
      `<span class="av-num">${Math.round(cur)} <em>/ ${max}</em></span>` +
    `</div>` +
    `<div class="av-bar"><div class="fill" style="width:${pct.toFixed(1)}%"></div></div>`;
  return row;
}

function aboutHeldTile(slot: string, icon: string, name: string, kind: string): HTMLElement {
  const tile = document.createElement("button");
  tile.type = "button";
  tile.className = `about-held-tile ${kind}`;
  tile.innerHTML =
    `<span class="ah-icon">${icon}</span>` +
    `<span class="ah-meta">` +
      `<span class="ah-slot">${slot}</span>` +
      `<span class="ah-name">${name}</span>` +
    `</span>`;
  if (kind !== "empty") {
    tile.addEventListener("click", () => { tile.classList.toggle("sel"); });
  } else {
    tile.disabled = true;
  }
  return tile;
}

/**
 * Mount a small WebGL character showcase into `host`. Own GL context (the
 * chardesign pattern) so the game's composer is untouched. Real buildPlayer
 * rig with current gear + held kit; idle auto-spin, drag to turn, double-click
 * to face the camera. Caller MUST dispose before tearing down the host.
 */
function mountAboutAvatar(host: HTMLElement): { dispose: () => void } {
  const canvas = document.createElement("canvas");
  canvas.className = "about-avatar-canvas";
  host.appendChild(canvas);

  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setClearColor(0x000000, 0);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(FOV, 1, 0.1, 40);
  // Portrait framing: full figure with headroom (pulled back so hair/hat isn't clipped).
  camera.position.set(0, 1.05, 3.25);
  camera.lookAt(0, 0.88, 0);

  scene.add(new THREE.HemisphereLight(0xbcd7ff, 0x5a4a32, 0.7));
  const dir = new THREE.DirectionalLight(0xfff2d6, 1.7);
  dir.position.set(2.2, 4.2, 2.6);
  scene.add(dir);
  scene.add(new THREE.AmbientLight(0xfff6e8, 0.32));
  // Soft fill from below-front so the parchment-dark stage still reads the toon ramp.
  const fill = new THREE.DirectionalLight(0xa8c4e8, 0.35);
  fill.position.set(-2, 1.2, 2);
  scene.add(fill);

  // Pedestal disc — grounds the figure on the dark stage.
  const pedestal = new THREE.Mesh(
    new THREE.CircleGeometry(0.62, 40),
    new THREE.MeshBasicMaterial({ color: 0xc9a227, transparent: true, opacity: 0.16 }),
  );
  pedestal.rotation.x = -Math.PI / 2;
  pedestal.position.y = 0.01;
  scene.add(pedestal);
  const ring = new THREE.Mesh(
    new THREE.RingGeometry(0.58, 0.64, 48),
    new THREE.MeshBasicMaterial({ color: 0x8a6f43, transparent: true, opacity: 0.35, side: THREE.DoubleSide }),
  );
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = 0.015;
  scene.add(ring);

  const gear = wornGear();
  const rig = buildPlayer(me.look, gear);
  const wt = equippedType();
  const shieldT = equippedShieldType();
  const toolT = equippedTool();
  // Held kit: weapon takes main hand; tool only when no weapon (channel exclusive).
  if (wt) setWeapon(rig, itemSpec(wt));
  else if (toolT) setTool(rig, itemSpec(toolT));
  if (shieldT) setShield(rig, itemSpec(shieldT));
  // Ready stance reads as a heroic showcase when armed; unarmed keeps a calm idle.
  if (wt) rig.userData.combatReady = true;
  rig.rotation.y = 0.42;
  scene.add(rig);

  let raf = 0;
  let last = 0;
  let disposed = false;
  let dragging = false;
  let dragX = 0;
  let idleT = 0.4; // brief pause before the first auto-spin
  let tSec = 0;

  function resize(): void {
    const r = host.getBoundingClientRect();
    const w = Math.max(1, Math.round(r.width));
    const h = Math.max(1, Math.round(r.height));
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }

  canvas.addEventListener("pointerdown", (e) => {
    dragging = true;
    dragX = e.clientX;
    canvas.setPointerCapture(e.pointerId);
    e.preventDefault();
  });
  canvas.addEventListener("pointermove", (e) => {
    if (!dragging) return;
    rig.rotation.y += (e.clientX - dragX) * 0.014;
    dragX = e.clientX;
    idleT = 1.8;
  });
  const endDrag = () => { dragging = false; };
  canvas.addEventListener("pointerup", endDrag);
  canvas.addEventListener("pointercancel", endDrag);
  canvas.addEventListener("dblclick", () => {
    rig.rotation.y = 0.42;
    idleT = 2.2;
  });
  // Don't let the journal page scroll while dragging the avatar.
  canvas.addEventListener("wheel", (e) => e.preventDefault(), { passive: false });

  function frame(now: number): void {
    if (disposed) return;
    raf = requestAnimationFrame(frame);
    const dt = last ? Math.min(0.05, (now - last) / 1000) : 0;
    last = now;
    tSec += dt;
    if (!dragging) {
      if (idleT > 0) idleT -= dt;
      else rig.rotation.y += dt * 0.48;
    }
    // Drive gait rest + weapon ready/carry so the showcase matches in-world kit.
    updatePlayer(rig, dt, false, tSec, false);
    renderer.render(scene, camera);
  }

  const observer = new ResizeObserver(() => resize());
  observer.observe(host);
  resize();
  raf = requestAnimationFrame(frame);

  return {
    dispose() {
      disposed = true;
      if (raf) { cancelAnimationFrame(raf); raf = 0; }
      observer.disconnect();
      scene.remove(rig);
      disposeGroup(rig);
      for (const m of [pedestal, ring]) {
        scene.remove(m);
        m.geometry.dispose();
        (m.material as THREE.Material).dispose();
      }
      renderer.dispose();
      canvas.remove();
    },
  };
}
